import { graphHooks } from '@tunasong/graph-lib/react'
import { Bolt, Home, Left, Right, Search, Shield, Star } from '@tunasong/icons'
import { selectIsAdmin, useCurrentUser, useSelector } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import {
  ShortcutBox,
  UserAvatar,
  getOS,
  shortcuts,
  useDeviceState,
  useFavorites,
  useLocation,
  useNavigate,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import { useEffect, useRef, type FC, type KeyboardEvent, type MouseEvent } from 'react'
import styles from './app-sidebar.module.css'
import { toAriaKeyShortcuts } from './aria-key-shortcuts.js'
import { HelpMenuButton } from '../help/help-menu.js'
import { SidebarSection } from './sidebar-section.js'
import { ToolsMenuItems } from './tools-menu-items.js'
import { getSyncStatusText, usePendingChangesCount } from './use-pending-changes-count.hook.js'

export interface AppSideBarProps {
  entity?: Persisted<Entity>
}

const minWidth = 120
const maxWidth = 400
const defaultWidth = 240
const keyboardStep = 16

const clampWidth = (width: number) => Math.min(maxWidth, Math.max(minWidth, width))

export const AppSideBar: FC<AppSideBarProps> = props => {
  const { entity } = props

  const { favorites } = useFavorites()
  const { userId } = useCurrentUser()
  const isAdmin = useSelector(selectIsAdmin)
  const { profile } = graphHooks.useProfile(userId)
  const pendingChanges = usePendingChangesCount()

  const { dialog: searchDialog, setOpen: setSearchOpen, open: searchOpen } = useSearchDialog(entity)

  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = `${location.pathname}${location.search}`

  const [collapsed = false, setCollapsed, collapsedReady] = useDeviceState<boolean>('sidebar.collapsed', false)
  const [width = defaultWidth, setWidth, widthReady] = useDeviceState<number>('sidebar.width', defaultWidth)
  const [favoritesOpen = true, setFavoritesOpen] = useDeviceState<boolean>('sidebar.favorites.open', true)
  const [toolsOpen = true, setToolsOpen] = useDeviceState<boolean>('sidebar.tools.open', true)
  const sidebarReady = collapsedReady && widthReady
  const cleanupRef = useRef<(() => void) | null>(null)

  // Clean up resize listeners on unmount
  useEffect(() => () => cleanupRef.current?.(), [])

  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      setWidth(clampWidth(startWidth + (moveEvent.clientX - startX)))
    }

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      cleanupRef.current = null
    }

    const handleMouseUp = () => cleanup()

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    cleanupRef.current = cleanup
  }

  const handleResizeKey = (e: KeyboardEvent) => {
    const delta = e.key === 'ArrowRight' ? keyboardStep : e.key === 'ArrowLeft' ? -keyboardStep : 0
    if (delta === 0) {
      return
    }
    e.preventDefault()
    setWidth(clampWidth(width + delta))
  }

  const handleExpandForTheory = () => {
    setToolsOpen(true)
    setCollapsed(false)
  }

  const navRow = (label: string, path: string, icon: FC) => {
    const Icon = icon
    const isCurrent = location.pathname === path
    return (
      <button
        type="button"
        onClick={() => void navigate(path)}
        className={cn(styles.row, isCurrent && styles['row--active'])}
        aria-current={isCurrent ? 'page' : undefined}
        title={label}
      >
        <span className={styles.icon} aria-hidden="true">
          <Icon />
        </span>
        <span className={cn(styles.label, styles.grow)}>{label}</span>
      </button>
    )
  }

  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar'
  const name = profile?.name ?? profile?.email ?? 'Account'
  const syncStatus = getSyncStatusText(pendingChanges)

  return (
    <nav
      aria-label="Main"
      className={cn(
        styles.sidebar,
        collapsed && styles['sidebar--collapsed'],
        !sidebarReady && styles['sidebar--no-transition']
      )}
      style={!collapsed ? { width } : undefined}
    >
      <div className={styles.header}>
        <span className={cn(styles.brand, styles.label)}>Tunasong</span>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          title={toggleLabel}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <Right size={18} /> : <Left size={18} />}
        </button>
      </div>

      <button
        type="button"
        className={cn(styles.search, searchOpen && styles['search--open'])}
        aria-keyshortcuts={toAriaKeyShortcuts(shortcuts.Search.hotkey, getOS())}
        title="Search"
        onClick={() => setSearchOpen(true)}
      >
        <span className={styles.icon} aria-hidden="true">
          <Search />
        </span>
        <span className={cn(styles.label, styles.grow)}>Search</span>
        <span className={styles.label} aria-hidden="true">
          <ShortcutBox shortcut={shortcuts.Search} showDescription={false} />
        </span>
      </button>

      <div className={styles.nav}>
        {navRow('Home', '/app', Home)}
        {navRow('Automations', '/app/automations', Bolt)}
        {/* Admin spec §3.4: admins only, absent rather than disabled for everyone else (Joni §6) */}
        {isAdmin ? navRow('Admin', '/app/admin', Shield) : null}

        {collapsed && favorites.length === 0 ? null : (
          <SidebarSection
            title="Favorites"
            open={favoritesOpen}
            onOpenChange={setFavoritesOpen}
            count={favorites.length}
            rail={collapsed}
          >
            {favorites.map(favorite => {
              const isCurrent = currentPath === favorite.path
              return (
                <button
                  type="button"
                  key={favorite.path}
                  className={cn(styles.row, isCurrent && styles['row--active'])}
                  aria-current={isCurrent ? 'page' : undefined}
                  title={favorite.name}
                  onClick={() => void navigate(favorite.path)}
                >
                  <span className={styles.tile} style={{ color: favorite.color }} aria-hidden="true" data-favorite-tile>
                    <favorite.Icon />
                  </span>
                  <span className={cn(styles.label, styles.grow)}>{favorite.name}</span>
                </button>
              )
            })}
            {favorites.length === 0 ? (
              <p className={styles.empty}>
                <span className={styles.icon} aria-hidden="true">
                  <Star size={14} />
                </span>
                <span>Star a song, setlist or page to keep it one click away.</span>
              </p>
            ) : null}
          </SidebarSection>
        )}

        <SidebarSection title="Tools" open={toolsOpen} onOpenChange={setToolsOpen} rail={collapsed}>
          <ToolsMenuItems variant="caption" rail={collapsed} onExpandRequest={handleExpandForTheory} />
        </SidebarSection>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.account}
          title={`${name}: ${syncStatus}`}
          onClick={() => void navigate('/app/profile')}
        >
          <span className={styles.avatar} aria-hidden="true">
            <UserAvatar size="tiny" userId={userId} link={false} profileCard={false} />
            {pendingChanges > 0 ? <span className={styles.pip} /> : null}
          </span>
          <span className={cn(styles.label, styles.who)}>
            <span className={styles.name}>{name}</span>
            <span className={styles.sync}>
              <span className={cn(styles.dot, pendingChanges > 0 && styles['dot--pending'])} aria-hidden="true" />
              {syncStatus}
            </span>
          </span>
        </button>
        {/* About the app, not any one page: Keyboard shortcuts and Contact support. Above the avatar in the rail. */}
        <HelpMenuButton className={styles.iconButton} />
        {/* Outside the button, where live regions are reliable. State, not count: edits are not read one by one. */}
        <span role="status" className={styles.liveStatus}>
          {pendingChanges > 0 ? 'Changes waiting to sync' : 'All changes synced'}
        </span>
      </div>

      {!collapsed ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          aria-valuenow={width}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          tabIndex={0}
          className={styles.resizeHandle}
          onMouseDown={handleResizeStart}
          onKeyDown={handleResizeKey}
        />
      ) : null}

      {searchDialog}
    </nav>
  )
}
